import { Fragment, useMemo, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { useI18n } from '../i18n/useI18n';
import { EmptyState } from './primitives';

/**
 * One table, everywhere there is a table.
 *
 * **Sorting is not an extra.** A board of fifty tenancies that cannot be
 * ordered is a board nobody reads a second time, and the way out of that was
 * previously to stop using a table at all — one card per tenancy, nine blocks
 * each, in whatever order the answer arrived. So every column that can be
 * ordered says how, and the header that orders it is a button with `aria-sort`
 * on its cell rather than a decoration a mouse discovers.
 *
 * **A row opens downwards.** What a row does not have space for goes under the
 * row and not into a panel over the table: this table exists to compare rows,
 * and a panel across it is the comparison taken away at the moment somebody
 * wanted it.
 *
 * **"Nothing here" and "nothing matched" are different answers.** The first is
 * about the fleet, the second about the filter, and only one of them is put
 * right by clearing a search box.
 */

export interface Column<Row> {
  /** Identifies the column to the sort, and nothing else. */
  key: string;
  header: string;
  /** Read out but not drawn. For a column of controls, whose heading is noise. */
  headerHidden?: boolean;
  cell: (row: Row) => ReactNode;
  align?: 'end';
  /** Dropped on a narrow screen. For a column that is context rather than the answer. */
  hideSm?: boolean;
  /** Given, the header orders by it. Absent, the column does not order. */
  sort?: (a: Row, b: Row) => number;
  /** Machine text: identifiers, versions, fingerprints. */
  mono?: boolean;
  /**
   * Context rather than the answer: a time, a count, a setting.
   *
   * The console's rule, in one flag. A cell that names another entity — a
   * person, a node, a resource, an identifier somebody will copy — keeps the
   * text colour; everything else recedes, so that scanning a table lands on the
   * things and not on the dates beside them.
   */
  muted?: boolean;
}

type Direction = 'asc' | 'desc';

interface Order {
  key: string;
  direction: Direction;
}

const ARIA_SORT: Record<Direction, 'ascending' | 'descending'> = {
  asc: 'ascending',
  desc: 'descending',
};

export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  total,
  initialSort,
  onRowClick,
  renderExpanded,
  empty,
  noMatch,
}: {
  /** Read to somebody who cannot see the table. */
  caption: string;
  columns: Column<Row>[];
  /** What is to be drawn — already filtered by whoever owns the filter. */
  rows: Row[];
  rowKey: (row: Row) => string;
  /**
   * How many rows there are before any filter. Absent means `rows` is all
   * there is, and then an empty table can only be an empty fleet.
   */
  total?: number;
  initialSort?: Order;
  /**
   * Given, the whole row leads somewhere.
   *
   * The first cell still carries a real link, and this is beside it rather than
   * instead of it: a `<tr>` is not reachable from a keyboard and cannot be
   * opened in another tab.
   */
  onRowClick?: (row: Row) => void;
  /** Given, every row opens. What is inside is the caller's. */
  renderExpanded?: (row: Row) => ReactNode;
  /** Nothing at all. `action` is the one thing to do from here, where there is one. */
  empty: { title: string; body?: string; action?: ReactNode };
  noMatch: { title: string; body?: string };
}) {
  const { t } = useI18n();

  const [order, setOrder] = useState<Order | null>(initialSort ?? null);
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set<string>());

  const sorted = useMemo(() => {
    if (order === null) return rows;
    const column = columns.find((candidate) => candidate.key === order.key);
    const compare = column?.sort;
    if (compare === undefined) return rows;
    const copy = [...rows];
    copy.sort((a, b) => (order.direction === 'asc' ? compare(a, b) : compare(b, a)));
    return copy;
  }, [rows, columns, order]);

  if (sorted.length === 0) {
    const words: { title: string; body?: string; action?: ReactNode } =
      total === undefined || total === 0 ? empty : noMatch;
    const action = words.action;
    return (
      <EmptyState
        title={words.title}
        {...(words.body === undefined ? {} : { body: words.body })}
        {...(action === undefined ? {} : { action })}
      />
    );
  }

  const span = columns.length + (renderExpanded === undefined ? 0 : 1);

  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <caption className="visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => {
              const sorting = order !== null && order.key === column.key;
              return (
                <th
                  key={column.key}
                  scope="col"
                  className={column.hideSm === true ? 'hide-sm' : undefined}
                  {...(column.sort === undefined
                    ? {}
                    : { 'aria-sort': sorting ? ARIA_SORT[order.direction] : 'none' })}
                >
                  {column.headerHidden === true ? (
                    <span className="visually-hidden">{column.header}</span>
                  ) : column.sort === undefined ? (
                    column.header
                  ) : (
                    <button
                      type="button"
                      className="th-sort"
                      onClick={() => {
                        setOrder(
                          sorting
                            ? { key: column.key, direction: order.direction === 'asc' ? 'desc' : 'asc' }
                            : { key: column.key, direction: 'asc' },
                        );
                      }}
                    >
                      {column.header}
                    </button>
                  )}
                </th>
              );
            })}
            {renderExpanded !== undefined && (
              <th scope="col">
                <span className="visually-hidden">{t('common.actions')}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const key = rowKey(row);
            const expanded = open.has(key);
            return (
              <Fragment key={key}>
                <tr
                  className={[expanded ? 'expanded' : '', onRowClick === undefined ? '' : 'clickable']
                    .filter(Boolean)
                    .join(' ')}
                  {...(onRowClick === undefined
                    ? {}
                    : {
                        onClick: (event: MouseEvent<HTMLTableRowElement>) => {
                          // A press on the link, the wave chooser or the button
                          // that opens the row is that control's, not the row's.
                          if ((event.target as HTMLElement).closest('a, button, select') !== null) {
                            return;
                          }
                          onRowClick(row);
                        },
                      })}
                >
                  {columns.map((column, index) => {
                    const classes = [
                      column.mono === true ? 't-mono' : '',
                      column.muted === true ? 'muted' : '',
                      column.hideSm === true ? 'hide-sm' : '',
                      column.align === 'end' ? 't-actions' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    const content = column.cell(row);
                    return index === 0 ? (
                      <th key={column.key} scope="row" className={classes === '' ? undefined : classes}>
                        {content}
                      </th>
                    ) : (
                      <td key={column.key} className={classes === '' ? undefined : classes}>
                        {content}
                      </td>
                    );
                  })}
                  {renderExpanded !== undefined && (
                    <td className="t-actions">
                      <button
                        type="button"
                        className="text-action"
                        aria-expanded={expanded}
                        onClick={() => {
                          const next = new Set(open);
                          if (expanded) next.delete(key);
                          else next.add(key);
                          setOpen(next);
                        }}
                      >
                        {t(expanded ? 'table.collapse' : 'table.expand')}
                      </button>
                    </td>
                  )}
                </tr>
                {renderExpanded !== undefined && expanded && (
                  <tr className="tbl-expand">
                    <td colSpan={span}>{renderExpanded(row)}</td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
